<template>
  <div class="product-gallery">
    <div class="product-gallery__main-container">
      <div
        v-if="badges"
        class="product-gallery__badges-container"
      >
        <product-badges
          :badges="badges"
          :product="product"
        />
      </div>

      <product-gallery-carousel
        class="product-gallery__main-carousel"
        :current-index="currentIndex"
        :current-namespace="currentNamespace"
        :loading="loading"
        namespace="gallery"
        :product="product"
        :sizes="sizes"
        type="main"
        :zoom="zoom"
        ratio="4:5"
        @update-current-index="handleUpdateCurrentIndex"
      />
    </div>

    <!-- Product gallery modal -->
    <overlay
      v-if="zoom"
      key="productGalleryModal"
      block-class="product-gallery-modal-overlay"
      direction="up"
      namespace="productGalleryModal"
      :show-overlay="false"
      :template="false"
      type="modal"
    >
      <template #body="overlay">
        <product-gallery-modal
          v-if="getComponentVisible('product-gallery-modal')"
          v-cloak
          :current-index="currentIndex"
          :current-namespace="currentNamespace"
          namespace="modal"
          :overlay="overlay"
          :product="product"
          @update-current-index="handleUpdateCurrentIndex"
          @update-current-namespace="handleUpdateCurrentNamespace"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Product gallery (product-gallery)
 * -----------------------------------------------------------------------------
 * Wrapper for product gallery carousel.
 *
 * @param {Boolean|Object} [badges] - Product badges.
 * @param {Number} currentIndex - Current carousel index.
 * @param {String} currentNamespace - Current carousel namespace.
 * @param {Boolean} [loading] - Loading state.
 * @param {Object} product - Local product object.
 * @param {Boolean|Null|Object} variant - Canvas formatted variant object.
 * @param {Boolean} [zoom] - Enable zoom functionality.
 *
 * @emits updateCurrentIndex - Emit update current carousel index event.
 * @emits updateCurrentNamespace - Emit update current carousel namespace event.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import ProductGalleryCarousel from '~async/product-gallery-carousel/product-gallery-carousel'
import ProductBadges from '~async/product-badges/product-badges.vue'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ProductGallery',

  components: {
    ProductBadges,
    Overlay,
    ProductGalleryCarousel,
    ProductGalleryModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.product-gallery-modal' */
        '~async/product-gallery-modal/product-gallery-modal'
      ),
    }),
  },

  props: {
    badges: {
      type: [Boolean, Object],
      default: false,
    },
    currentIndex: {
      type: Number,
      default: 0,
    },
    currentNamespace: {
      type: String,
      default: 'gallery',
      validator: (value) => {
        return ['gallery', 'modal'].includes(value)
      },
    },
    loading: {
      type: Boolean,
      default: false,
    },
    product: {
      type: [Boolean, Object],
      default: false,
    },
    variant: {
      type: [Boolean, null, Object],
      default: () => ({}),
    },
    zoom: {
      type: Boolean,
      default: true,
    },
  },

  emits: {
    'update-current-index': (payload) => typeof payload === 'number',
    'update-current-namespace': (payload) => ['gallery', 'modal'].includes(payload),
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute badge styles.
     * @returns {Object}
     */
    badgeStyles(colour) {
      return { backgroundColor: colour }
    },

    /**
     * Compute responsive image sizes.
     * @returns {String}
     */
    sizes() {
      return `(min-width: ${breakpoints.l}px) 50vw, 100vw`
    },
  },

  watch: {

    /**
     * Watch variant to show matching image if it exists.
     */
    variant() {
      if (!this.variant) {
        return
      }

      /**
       * Match media (either media or images array) with associated variant.
       */
      const matchedImageIndex = this.product?.media?.findIndex((media) => {
        return media.variant === this.variant.id
      })

      if (matchedImageIndex < 0) {
        return
      }

      this.$emit('update-current-index', matchedImageIndex)
    },
  },

  methods: {

    /**
     * Emit update current carousel index event.
     * @param {Number} newIndex - New media index.
     */
    handleUpdateCurrentIndex(newIndex) {
      this.$emit('update-current-index', newIndex)
    },

    /**
     * Emit update current carousel namespace event.
     * @param {Number} newNamespace - New active namespace.
     */
    handleUpdateCurrentNamespace(newNamespace) {
      this.$emit('update-current-namespace', newNamespace)
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './product-gallery';
</style>
